<template>
    <Line
        id="graph-line-chart-canvas"
        :data="chartData"
        :options="chartOptions"
    />
</template>

<script lang="ts" setup>
import { computed, ref, onBeforeUnmount } from 'vue'
import { palette } from '@/utils/palette'
import { Line } from 'vue-chartjs'
import { Chart, registerables } from 'chart.js'
import { GraphData } from '@/entities/frontlineManagerDash'
import { formatScoreWithPercentage } from '@/utils/number'

Chart.register(...registerables)

interface Props {
    graphData: GraphData
    metric: string
    metricTitle?: string
    isFiveScore: boolean
    csatInPercentage: boolean
    decimals: {
        nps: number
        csat: number
        fivestar: number
        ces: number
    }
}
const props = defineProps<Props>()

const gradientInitialised = ref(false)

const chartData = computed(() => {
    const datasets = [
        {
            backgroundColor: 'transparent',
            borderColor: palette.pPrimary500,
            borderWidth: 2,
            clip: false,
            cubicInterpolationMode: 'monotone',
            data: props.graphData.nps,
            fill: 'start', // Fill the area below the line (fill is set in gradientPlugin below)
            hitRadius: 20, // Hover area radius that triggers tooltip visibility
            hoverBackgroundColor: palette.pPrimary600,
            hoverRadius: 6, // Size of hover dot
            pointRadius: 0,
        },
    ]

    const goal = props.graphData.target
    if (goal) {
        // Draw a straight dotted green line to display the score goal
        datasets.push({
            borderColor: palette.pSuccess500,
            borderDash: [5, 5],
            borderWidth: 2,
            data: new Array(props.graphData.nps.length).fill(goal),
            fill: false,
            lineTension: 0,
            pointRadius: 0,
            tension: 0,
        })
    }

    return {
        labels: props.graphData.labels,
        datasets,
    }
})

const scaleOptions = {
    grid: { display: false },
    border: { display: false },
}
const axisLabelFont = {
    family: 'Roboto',
    size: 12,
    weight: 500,
    color: palette.pGray600,
}
const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            enabled: false, // Hide the default tooltip, custom tooltip defined in 'external' below
            filter: (tooltipItem) => {
                // Show tooltips for the first dataset only (exclude the goal line dataset)
                return tooltipItem.datasetIndex === 0
            },
            callbacks: {
                label: (context) => {
                    const index = context.dataIndex
                    return `<div class="purple-dot"></div>
                            <span class="score">${props.metricTitle ?? props.metric} ${formatScoreWithPercentage(props.graphData.nps[index], props.isFiveScore, props.csatInPercentage, props.metric, props.decimals)}</span>
                            <span>${props.graphData.hoverDate[index]} • ${props.graphData.totalResponded[index]} responses</span>`
                },
            },
            external: (context) => {
                // Custom HTML tooltip on hover of score line
                // https://www.chartjs.org/docs/latest/samples/tooltip/html.html

                const tooltopElId = 'frontline-manager-dash-chartjs-tooltip'
                let tooltipEl = document.getElementById(tooltopElId)

                // Create element on first render
                if (!tooltipEl) {
                    tooltipEl = document.createElement('div')
                    tooltipEl.id = tooltopElId
                    document.body.appendChild(tooltipEl)
                }

                // Hide if no tooltip
                const tooltipModel = context.tooltip
                if (tooltipModel.opacity === 0) {
                    tooltipEl.style.opacity = 0
                    return
                }

                // Set text defined in 'label' callback
                const tooltipBody = tooltipModel.body?.[0]?.lines?.[0]
                if (tooltipBody) {
                    tooltipEl.innerHTML = tooltipBody
                }

                // Set custom absolute position for tooltip
                const position = context.chart.canvas.getBoundingClientRect()
                tooltipEl.style.opacity = 1

                tooltipEl.style.left =
                    position.left +
                    window.scrollX +
                    tooltipModel.caretX -
                    tooltipEl.scrollWidth / 2 +
                    'px'
                tooltipEl.style.top =
                    position.top +
                    window.scrollY +
                    tooltipModel.caretY -
                    (tooltipEl.scrollHeight + 14) +
                    'px'
            },
        },
    },
    scales: {
        x: {
            ...scaleOptions,
            ticks: {
                font: axisLabelFont,
                callback: (value, index) => {
                    return props.graphData?.labels[index]?.toUpperCase() ?? ''
                },
            },
        },
        y: {
            ...scaleOptions,
            ticks: {
                stepSize: 1,
                padding: 8,
                font: axisLabelFont,
                callback: (value, index, ticks) => {
                    if (props.graphData?.nps?.length <= 0) return null

                    // Only show y-axis labels for the ticks that are closest to smallest
                    // and largest values from props.graphData.nps
                    const minValue = Math.min(...props.graphData.nps)
                    const maxValue = Math.max(...props.graphData.nps)

                    const tickClosestToValue = (someValue) => {
                        return ticks.reduce((prev, curr) => {
                            return Math.abs(curr.value - someValue) <
                                Math.abs(prev.value - someValue)
                                ? curr
                                : prev
                        })
                    }

                    if (
                        value === tickClosestToValue(minValue).value ||
                        value === tickClosestToValue(maxValue).value
                    ) {
                        return formatScoreWithPercentage(
                            value,
                            props.isFiveScore,
                            props.csatInPercentage,
                            props.metric,
                            props.decimals
                        )
                    }
                    return null
                },
            },
        },
    },
}

const gradientPlugin = {
    id: 'gradientPlugin',
    afterDraw: (chart) => {
        if (gradientInitialised.value || !chart) return

        gradientInitialised.value = true
        const ctx = chart.ctx
        const yScale = chart.scales.y

        // Determine the pixel positions of the lowest and highest values
        const minValue = yScale.min
        const maxValue = yScale.max
        const minY = yScale.getPixelForValue(minValue)
        const maxY = yScale.getPixelForValue(maxValue)

        // Create a gradient between minY and maxY
        const gradient = ctx.createLinearGradient(0, maxY, 0, minY)
        gradient.addColorStop(0, 'rgba(127, 86, 217, 0.2)') // Top color
        gradient.addColorStop(1, 'rgba(127, 86, 217, 0)') // Bottom color

        // Update the score line background color
        if (chart?.data?.datasets[0]) {
            chart.data.datasets[0].backgroundColor = gradient ?? 'transparent'
        }

        chart.update('none')
    },
    beforeDestroy: () => {
        gradientInitialised.value = false
    },
}

Chart.register(gradientPlugin)

onBeforeUnmount(() => {
    Chart.unregister(gradientPlugin)
})
</script>

<style lang="less">
@import '~@/styles/rain/variables.less';
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/prime-vue/colours.less';

// Unscoped styling to target ChartJS elements
#frontline-manager-dash-chartjs-tooltip {
    background: white;
    position: absolute;
    display: flex;
    align-items: center;
    gap: @spacing-md;
    padding: @spacing-md @spacing-lg;
    color: @text-secondary;
    font-family: @fontFamily;
    font-size: @fontSize-sm;
    line-height: @lineHeight-base;
    box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.25);
    transition: opacity 0.3s ease-in-out;
    border-radius: @radius-md;
    pointer-events: none;

    @dotSize: 8px;
    .purple-dot {
        width: @dotSize;
        height: @dotSize;
        background: var(--p-primary-500);
        border-radius: 50%;
    }

    .score {
        font-weight: @fontWeight-medium;
    }
}
</style>
